import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Star, TrendingUp, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestCRM = () => {
  const { t } = useTranslation();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API}/guests`);
      setGuests(response.data);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Error loading guests");
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    if (guests.length === 0) {
      toast.error("Geen gasten om te exporteren");
      return;
    }

    // Create CSV content
    const headers = ["Naam", "Email", "Telefoon", "Bezoeken", "No-shows", "VIP", "Allergieën"];
    const rows = guests.map(guest => [
      guest.name,
      guest.email,
      guest.phone,
      guest.visit_count || 0,
      guest.no_show_count || 0,
      guest.vip_status ? "Ja" : "Nee",
      guest.allergies || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `gasten_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${guests.length} gasten geëxporteerd`);
  };

  const toggleVIP = async (guestId, currentStatus) => {
    try {
      await axios.put(`${API}/guests/${guestId}?vip_status=${!currentStatus}`);
      setGuests(guests.map(g => 
        g.id === guestId ? { ...g, vip_status: !currentStatus } : g
      ));
      toast.success("VIP status updated");
    } catch (error) {
      console.error("Error updating VIP status:", error);
      toast.error("Error updating VIP status");
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="guest-crm-view">
      <div className="p-6 border-b border-white/10 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-gray-300" data-testid="guest-crm-title">{t('dashboard.guests')}</h2>
          <p className="text-sm text-gray-300">Manage guest profiles and history</p>
        </div>
        <Button
          onClick={downloadExcel}
          className="bg-brand-gold text-black hover:bg-[#F0C74A] flex items-center gap-2"
          data-testid="export-guests-btn"
        >
          <Download className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="text-center py-8 text-gray-300">Loading...</div>
        ) : guests.length === 0 ? (
          <div className="text-center py-8 text-gray-300" data-testid="no-guests">No guests yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {guests.map((guest, index) => (
              <div 
                key={guest.id} 
                className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors relative"
                data-testid={`guest-card-${index}`}
              >
                <button
                  onClick={async () => {
                    if (window.confirm(`Weet u zeker dat u ${guest.name} wilt verwijderen?`)) {
                      try {
                        await axios.delete(`${API}/guests/${guest.id}`);
                        setGuests(guests.filter(g => g.id !== guest.id));
                        toast.success("Gast verwijderd");
                      } catch (error) {
                        console.error("Error deleting guest:", error);
                        toast.error("Fout bij verwijderen gast");
                      }
                    }
                  }}
                  className="absolute top-2 right-2 p-1 text-gray-500 hover:text-red-400 transition-colors"
                  data-testid={`delete-guest-${index}`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                
                <div className="flex justify-between items-start mb-3 pr-8">
                  <div>
                    <h3 className="font-semibold text-gray-300" data-testid={`guest-name-${index}`}>{guest.name}</h3>
                    <p className="text-sm text-gray-300" data-testid={`guest-email-${index}`}>{guest.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={guest.vip_status ? "default" : "outline"}
                    onClick={() => toggleVIP(guest.id, guest.vip_status)}
                    className={guest.vip_status ? "bg-brand-gold text-gray-300" : ""}
                    data-testid={`vip-toggle-${index}`}
                  >
                    <Star className="w-4 h-4" fill={guest.vip_status ? "currentColor" : "none"} />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Visits</p>
                    <p className="text-gray-300 font-semibold" data-testid={`guest-visits-${index}`}>{guest.visit_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">No-shows</p>
                    <p className="text-red-400 font-semibold" data-testid={`guest-noshows-${index}`}>{guest.no_show_count || 0}</p>
                  </div>
                </div>

                {guest.allergies && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-xs text-gray-500">Allergies</p>
                    <p className="text-sm text-gray-300" data-testid={`guest-allergies-${index}`}>{guest.allergies}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestCRM;